/**
 * Pure helpers for the phone Days agenda (components/mobile/MobileDaysAgenda.svelte,
 * plans/mobile-v2.md §3.3): group the visible weeks into agenda days, pick the
 * strip's dots, and place a project in a week. Dates are `YYYY-MM-DD` strings and
 * are read as calendar dates, so the viewer's time zone never shifts them.
 */
import type { CalendarEvent, CalendarEventType, Project } from './types';
import { eventColor } from './eventColors';
import {
  MS_PER_DAY,
  eventDayIndex,
  eventsForDay,
  formatCalendarDate,
  getTodayStr,
  getWeekEnd,
  getWeekNumber,
  getWeekStart,
  parseCalendarDate,
  sortEventsForDisplay,
  toDateStr,
} from './utils';
import { formatShortDateRange } from './dateRangeLabel';

/** Most dots one strip day shows. */
export const MAX_STRIP_DOTS = 3;

/** One event on one agenda day. A multi-day event appears on each of its days. */
export interface AgendaEvent {
  event: CalendarEvent;
  /** 1-based position within a multi-day event (`Day 2/3`); 1 when it lasts one day. */
  dayNumber: number;
  totalDays: number;
}

export interface AgendaDay {
  date: string;
  /** `Sun` … `Sat` */
  weekday: string;
  dayOfMonth: number;
  /** `Sep`, shown on the 1st of a month and the first day of a week. */
  monthLabel: string;
  isToday: boolean;
  events: AgendaEvent[];
}

export interface AgendaWeek {
  weekKey: string;
  weekNumber: number;
  /** `Sep 27 – Oct 3` */
  rangeLabel: string;
  days: AgendaDay[];
}

const TYPE_LABELS: Record<CalendarEventType, string> = {
  appointment: 'Appt',
  event: 'Event',
  deadline: 'Deadline',
  travel: 'Travel',
};

/** Short type name for an agenda row. */
export function eventTypeLabel(type: string): string {
  return TYPE_LABELS[type as CalendarEventType] ?? 'Event';
}

/**
 * The visible weeks as agenda days, in order. `visibleWeeks` and `events` are the
 * Dashboard's props (the same ones the desktop grid reads). `today` defaults to
 * the local date.
 */
export function buildAgendaWeeks(
  visibleWeeks: string[],
  events: CalendarEvent[],
  today: string = getTodayStr(),
): AgendaWeek[] {
  return visibleWeeks.map((weekKey) => {
    const start = getWeekStart(weekKey);
    const days: AgendaDay[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setUTCDate(start.getUTCDate() + i);
      const date = toDateStr(day);
      days.push({
        date,
        weekday: formatCalendarDate(day, { weekday: 'short' }),
        dayOfMonth: day.getUTCDate(),
        monthLabel: formatCalendarDate(day, { month: 'short' }),
        isToday: date === today,
        events: sortEventsForDisplay(eventsForDay(events, date)).map((event) => {
          const { dayIndex, totalDays } = eventDayIndex(event, date);
          return { event, dayNumber: dayIndex, totalDays };
        }),
      });
    }
    return {
      weekKey,
      weekNumber: getWeekNumber(weekKey),
      rangeLabel: formatShortDateRange(days[0].date, days[6].date, today),
      days,
    };
  });
}

/** The strip dots for a day: the event types' calendar colours, at most `max`. */
export function eventDots(events: AgendaEvent[], max: number = MAX_STRIP_DOTS): string[] {
  return events.slice(0, max).map(({ event }) => eventColor(event.type));
}

/**
 * The week the agenda shows at the top: the last week whose top edge has reached
 * `offset` below the scroll position (`tops` are the weeks' offsets in order).
 */
export function activeWeekIndex(tops: number[], scrollTop: number, offset: number): number {
  let active = 0;
  for (let i = 0; i < tops.length; i++) {
    if (tops[i] <= scrollTop + offset) active = i;
    else break;
  }
  return active;
}

/**
 * The week to show first: the one with today, else the Dashboard's current week,
 * else the first visible one.
 */
export function initialWeekKey(
  visibleWeeks: string[],
  currentWeek: string,
  today: string = getTodayStr(),
): string {
  const withToday = visibleWeeks.find((weekKey) => {
    const start = toDateStr(getWeekStart(weekKey));
    const end = toDateStr(getWeekEnd(weekKey));
    return start <= today && today <= end;
  });
  if (withToday) return withToday;
  return visibleWeeks.includes(currentWeek) ? currentWeek : (visibleWeeks[0] ?? currentWeek);
}

function sundayOf(date: string): number {
  const d = parseCalendarDate(date);
  return d.getTime() - d.getUTCDay() * MS_PER_DAY;
}

/** Whether a scheduled project belongs to a week: its dates overlap it, or (undated) its month. */
export function projectInWeek(project: Project, weekKey: string): boolean {
  const start = toDateStr(getWeekStart(weekKey));
  const end = toDateStr(getWeekEnd(weekKey));
  if (project.startDate && project.endDate)
    return project.startDate <= end && project.endDate >= start;
  if (!project.monthKey) return false;
  // Week → month by its Thursday, the convention of the month-scale Days grid.
  const thursday = parseCalendarDate(start);
  thursday.setUTCDate(thursday.getUTCDate() + 4);
  return project.monthKey === toDateStr(thursday).slice(0, 7);
}

/** `{ index: 4, total: 6 }` for the 4th week of a 6-week project; null for a one-week or undated one. */
export function projectWeekPosition(
  project: Project,
  weekKey: string,
): { index: number; total: number } | null {
  if (!project.startDate || !project.endDate) return null;
  const first = sundayOf(project.startDate);
  const total = Math.round((sundayOf(project.endDate) - first) / (7 * MS_PER_DAY)) + 1;
  if (total < 2) return null;
  const index = Math.round((getWeekStart(weekKey).getTime() - first) / (7 * MS_PER_DAY)) + 1;
  return index >= 1 && index <= total ? { index, total } : null;
}
